import React, { useState } from 'react'
import ScoreCard from './ScoreCard.jsx'
import FinancialSummary from './FinancialSummary.jsx'
import MonthlyChart from './MonthlyChart.jsx'
import Recommendations from './Recommendations.jsx'
import ClimateRiskPanel from './ClimateRiskPanel.jsx'

const SECTIONS = ['Overview', 'Yield', 'Financial', 'Climate', 'Recommendations']

export default function AssessmentPanel({ assessment, loading, error }) {
  const [section, setSection] = useState('Overview')

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner" />
        <span>Running assessment</span>
        <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>Fetching resource data and running yield model…</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card">
        <div className="alert alert-danger">{error}</div>
      </div>
    )
  }

  if (!assessment) return null

  const loc    = assessment.location   ?? {}
  const assess = assessment.assessment ?? {}
  const site   = assessment.site       ?? {}
  const grid   = assessment.grid       ?? {}
  const yld    = assessment.yield      ?? {}
  const res    = assessment.resource   ?? {}

  const techLabel = assess.technology === 'solar_pv' ? 'Solar PV' : 'Wind'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>

      {/* Location */}
      <div className="loc-card">
        <div className="loc-card__name">
          {loc.country} — {loc.latitude?.toFixed(4)}°, {loc.longitude?.toFixed(4)}°
        </div>
        <div className="loc-card__meta">
          <span>{techLabel}</span>
          <span className="loc-card__dot" />
          <span>{assess.capacity_mw} MW</span>
        </div>
      </div>

      {/* Alerts */}
      {site.is_excluded && (
        <div className="alert alert-danger">
          <strong>Site excluded:</strong> {site.exclusion_reasons?.join('; ')}
        </div>
      )}
      {site.warnings?.length > 0 && (
        <div className="alert alert-warn">
          {site.warnings.slice(0, 2).join('; ')}
        </div>
      )}

      {/* Section tabs */}
      <div className="panel-tabs">
        {SECTIONS.map(s => (
          <button
            key={s}
            className={`panel-tab${section === s ? ' active' : ''}`}
            onClick={() => setSection(s)}
          >
            {s}
          </button>
        ))}
      </div>

      {section === 'Overview' && (
        <>
          <ScoreCard scores={assessment.scores} />
          <div className="card">
            <div className="card__title">Resource & Site</div>
            <table className="data-table">
              <tbody>
                {assess.technology === 'solar_pv' ? (
                  <tr>
                    <td>GHI (annual)</td>
                    <td>{res.solar_ghi_annual_kwh_m2?.toLocaleString()} kWh/m²</td>
                  </tr>
                ) : (
                  <>
                    <tr><td>Mean wind speed</td><td>{res.wind_speed_annual_ms?.toFixed(2)} m/s</td></tr>
                    <tr><td>Wind power density</td><td>{res.wind_power_density_w_m2?.toFixed(0)} W/m²</td></tr>
                  </>
                )}
                <tr><td>Nearest substation</td><td>{grid.nearest_substation_km?.toFixed(1)} km</td></tr>
                <tr><td>Grid reliability</td><td>{(grid.grid_reliability * 100)?.toFixed(1)}%</td></tr>
                <tr><td>Land use</td><td>{site.land_use_class}</td></tr>
                <tr><td>Slope</td><td>{site.terrain_slope_deg?.toFixed(1)}°</td></tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      {section === 'Yield' && (
        <>
          <div className="card">
            <div className="card__title">Annual Yield</div>
            <table className="data-table">
              <tbody>
                <tr><td>P50 (median)</td><td>{yld.p50_annual_mwh?.toLocaleString()} MWh</td></tr>
                <tr><td>P90 (conservative)</td><td>{yld.p90_annual_mwh?.toLocaleString()} MWh</td></tr>
                <tr><td>Capacity factor</td><td>{(yld.capacity_factor * 100)?.toFixed(1)}%</td></tr>
                <tr><td>Specific yield</td><td>{yld.specific_yield_kwh_kwp?.toLocaleString()} kWh/kWp</td></tr>
              </tbody>
            </table>
          </div>
          <MonthlyChart monthly={yld.monthly_mwh} technology={assess.technology} />
        </>
      )}

      {section === 'Financial' && (
        <FinancialSummary financial={assessment.financial} />
      )}

      {section === 'Climate' && (
        <ClimateRiskPanel climateRisk={assessment.climate_risk} />
      )}

      {section === 'Recommendations' && (
        <Recommendations recommendations={assess.recommendations} explanation={assess.explanation} />
      )}
    </div>
  )
}
